import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PageTitle from "../../core/page-title"

import RenderFormFields from '../../core/forms'
import {useNavigate} from 'react-router'

import { useAuth } from '../../../AuthContext';
import { socket, socketUseEffect } from '../../../socket';

const dict = {
    en: {
        pageTitle: "Account Settings",
        tzLoading: "Loading time zones...",
        tzEmpty: "No time zones available",
        tzSelect: "Select time zone",
        firstNameRequired: "First name is required.",
        lastNameRequired: "Last name is required.",
        emailRequired: "Email is required.",
        emailInvalid: "Invalid email.",
        currentPassRequired: "Current password is required.",
        newPassRequired: "New password is required.",
        confirmPassRequired: "Please confirm your new password.",
        passwordMismatch: "Passwords do not match.",
        passwordTooShort: "Password must be at least 8 characters.",
        missingToken: "Missing authentication token.",
        saveSuccess: "Profile saved successfully.",
        saveErrorGeneric: "Unable to save profile.",
        twoFaCardTitle: "Two-factor Authentication",
        twoFaLead: "Protect your account with a second factor at sign in. Toggle and save the profile to apply changes.",
        twoFaFooter: "Additional configuration steps (auth apps, backup email, SMS recovery) will be available after enabling 2FA.",
        twoFaEnabled: "Enabled",
        twoFaDisabled: "Disabled",
        tabAccount: "Account",
        tabSecurity: "Security",
        groupPersonal: "Personal Details",
        groupPersonalSub: "Edit and save your personal info here.",
        firstNameLabel: "First Name",
        lastNameLabel: "Last Name",
        emailLabel: "Email",
        phoneLabel: "Phone",
        locationLabel: "Location",
        addressLabel: "Address",
        groupDateTime: "Date & Time",
        groupDateTimeSub: "Time zones and calendar display settings.",
        timeZoneLabel: "Time zone",
        groupPassword: "Change Password",
        groupPasswordSub: "Confirm your password changes here.",
        currentPasswordLabel: "Current Password",
        newPasswordLabel: "New Password",
        confirmPasswordLabel: "Confirm Password",
        groupDevices: "Devices",
        whitelistTitle: "2FA whitelist",
        whitelistDesc: "Login attempts from these IPs skip the second factor. Use this only for secure office networks.",
        ipAddressLabel: "IP address",
        manualEntry: "Manual entry",
        useMyIp: "Use my IP",
        descriptionLabel: "Description",
        descriptionPlaceholder: "Office, VPN, etc.",
        addIp: "Add IP",
        loadingWhitelist: "Loading whitelist...",
        tableHeaderIp: "IP",
        tableHeaderDescription: "Description",
        tableHeaderActions: "Actions",
        whitelistEmpty: "No IPs added yet.",
        edit: "Edit",
        delete: "Delete",
        ipPlaceholder: "192.0.2.1",
        ipRequired: "IP is required.",
        promptIpNote: "Add a description for this IP",
        confirmRemoveIp: (ip) => `Remove ${ip} from the whitelist?`,
        whitelistLoadError: "Unable to load whitelist.",
        ipAdded: "IP added.",
        ipAddFailed: "Failed to add IP.",
        entryUpdated: "Entry updated.",
        entryUpdateFailed: "Failed to update entry.",
        entryRemoved: "Entry removed.",
        entryRemoveFailed: "Failed to remove entry.",
        devicesTitle: "Active devices",
        devicesSubtitle: "Manage tokens that currently have access to your account.",
        refresh: "Refresh",
        signOutAll: "Sign out from all devices",
        loadingDevices: "Loading devices...",
        noActiveSessions: "No active sessions were found.",
        unnamedDevice: "Unnamed device",
        currentBadge: "Current",
        ipUnavailable: "IP unavailable",
        lastActivePrefix: "Last active:",
        rename: "Rename",
        signOut: "Sign out",
        promptDeviceName: "Enter a name for this device",
        confirmSignOutDevice: "Sign out this device?",
        confirmSignOutAll: "Sign out from all devices?",
        devicesLoadError: "Unable to load active devices.",
        deviceRenameSuccess: "Device name updated.",
        deviceRenameError: "Failed to update device.",
        deviceRevokeSuccess: "Device signed out.",
        deviceRevokeError: "Failed to sign out device.",
        deviceRevokeAllSuccess: "Signed out from all devices.",
        deviceRevokeAllError: "Failed to sign out from all devices.",
        noDataDash: "—",
    },
    ru: {
        pageTitle: "Настройки аккаунта",
        tzLoading: "Загрузка часовых поясов...",
        tzEmpty: "Нет доступных часовых поясов",
        tzSelect: "Выберите часовой пояс",
        firstNameRequired: "Имя обязательно.",
        lastNameRequired: "Фамилия обязательна.",
        emailRequired: "Email обязателен.",
        emailInvalid: "Некорректный email.",
        currentPassRequired: "Текущий пароль обязателен.",
        newPassRequired: "Новый пароль обязателен.",
        confirmPassRequired: "Подтвердите новый пароль.",
        passwordMismatch: "Пароли не совпадают.",
        passwordTooShort: "Пароль должен содержать минимум 8 символов.",
        missingToken: "Отсутствует токен авторизации.",
        saveSuccess: "Профиль успешно сохранен.",
        saveErrorGeneric: "Не удалось сохранить профиль.",
        twoFaCardTitle: "Двухфакторная аутентификация",
        twoFaLead: "Защитите аккаунт вторым фактором при входе. Переключите и сохраните профиль, чтобы применить изменения.",
        twoFaFooter: "Дополнительные шаги (auth-приложения, резервный email, SMS) будут доступны после включения 2FA.",
        twoFaEnabled: "Включено",
        twoFaDisabled: "Выключено",
        tabAccount: "Аккаунт",
        tabSecurity: "Безопасность",
        groupPersonal: "Личные данные",
        groupPersonalSub: "Здесь можно отредактировать и сохранить личную информацию.",
        firstNameLabel: "Имя",
        lastNameLabel: "Фамилия",
        emailLabel: "Email",
        phoneLabel: "Телефон",
        locationLabel: "Город",
        addressLabel: "Адрес",
        groupDateTime: "Дата и время",
        groupDateTimeSub: "Настройки часовых поясов и отображения календаря.",
        timeZoneLabel: "Часовой пояс",
        groupPassword: "Смена пароля",
        groupPasswordSub: "Подтвердите изменения пароля здесь.",
        currentPasswordLabel: "Текущий пароль",
        newPasswordLabel: "Новый пароль",
        confirmPasswordLabel: "Подтвердите пароль",
        groupDevices: "Устройства",
        whitelistTitle: "Белый список 2FA",
        whitelistDesc: "Попытки входа с этих IP пропускают второй фактор. Используйте только для защищенных сетей.",
        ipAddressLabel: "IP-адрес",
        manualEntry: "Ввести вручную",
        useMyIp: "Использовать мой IP",
        descriptionLabel: "Описание",
        descriptionPlaceholder: "Офис, VPN и т.п.",
        addIp: "Добавить IP",
        loadingWhitelist: "Загрузка белого списка...",
        tableHeaderIp: "IP",
        tableHeaderDescription: "Описание",
        tableHeaderActions: "Действия",
        whitelistEmpty: "IP-адреса пока не добавлены.",
        edit: "Редактировать",
        delete: "Удалить",
        ipPlaceholder: "192.0.2.1",
        ipRequired: "IP обязателен.",
        promptIpNote: "Добавьте описание для этого IP",
        confirmRemoveIp: (ip) => `Удалить ${ip} из белого списка?`,
        whitelistLoadError: "Не удалось загрузить белый список.",
        ipAdded: "IP добавлен.",
        ipAddFailed: "Не удалось добавить IP.",
        entryUpdated: "Запись обновлена.",
        entryUpdateFailed: "Не удалось обновить запись.",
        entryRemoved: "Запись удалена.",
        entryRemoveFailed: "Не удалось удалить запись.",
        devicesTitle: "Активные устройства",
        devicesSubtitle: "Управляйте токенами, которые имеют доступ к вашему аккаунту.",
        refresh: "Обновить",
        signOutAll: "Выйти на всех устройствах",
        loadingDevices: "Загрузка устройств...",
        noActiveSessions: "Активных сессий не найдено.",
        unnamedDevice: "Безымянное устройство",
        currentBadge: "Текущий",
        ipUnavailable: "IP недоступен",
        lastActivePrefix: "Последняя активность:",
        rename: "Переименовать",
        signOut: "Выйти",
        promptDeviceName: "Введите имя для этого устройства",
        confirmSignOutDevice: "Выйти из этого устройства?",
        confirmSignOutAll: "Выйти со всех устройств?",
        devicesLoadError: "Не удалось загрузить активные устройства.",
        deviceRenameSuccess: "Имя устройства обновлено.",
        deviceRenameError: "Не удалось обновить устройство.",
        deviceRevokeSuccess: "Устройство отключено.",
        deviceRevokeError: "Не удалось отключить устройство.",
        deviceRevokeAllSuccess: "Выход выполнен на всех устройствах.",
        deviceRevokeAllError: "Не удалось выйти на всех устройствах.",
        noDataDash: "—",
    },
    ua: {
        pageTitle: "Налаштування облікового запису",
        tzLoading: "Завантаження часових поясів...",
        tzEmpty: "Немає доступних часових поясів",
        tzSelect: "Оберіть часовий пояс",
        firstNameRequired: "Ім'я є обов'язковим.",
        lastNameRequired: "Прізвище є обов'язковим.",
        emailRequired: "Email є обов'язковим.",
        emailInvalid: "Некоректний email.",
        currentPassRequired: "Поточний пароль обов'язковий.",
        newPassRequired: "Новий пароль обов'язковий.",
        confirmPassRequired: "Підтвердіть новий пароль.",
        passwordMismatch: "Паролі не збігаються.",
        passwordTooShort: "Пароль має містити щонайменше 8 символів.",
        missingToken: "Відсутній токен автентифікації.",
        saveSuccess: "Профіль успішно збережено.",
        saveErrorGeneric: "Не вдалося зберегти профіль.",
        twoFaCardTitle: "Двофакторна аутентифікація",
        twoFaLead: "Захистіть обліковий запис другим фактором під час входу. Увімкніть і збережіть профіль, щоб застосувати зміни.",
        twoFaFooter: "Додаткові кроки (auth-додатки, резервний email, SMS) будуть доступні після ввімкнення 2FA.",
        twoFaEnabled: "Увімкнено",
        twoFaDisabled: "Вимкнено",
        tabAccount: "Обліковий запис",
        tabSecurity: "Безпека",
        groupPersonal: "Особисті дані",
        groupPersonalSub: "Редагуйте та зберігайте особисту інформацію тут.",
        firstNameLabel: "Ім'я",
        lastNameLabel: "Прізвище",
        emailLabel: "Email",
        phoneLabel: "Телефон",
        locationLabel: "Місто",
        addressLabel: "Адреса",
        groupDateTime: "Дата та час",
        groupDateTimeSub: "Налаштування часових поясів і відображення календаря.",
        timeZoneLabel: "Часовий пояс",
        groupPassword: "Зміна пароля",
        groupPasswordSub: "Підтвердіть зміну пароля тут.",
        currentPasswordLabel: "Поточний пароль",
        newPasswordLabel: "Новий пароль",
        confirmPasswordLabel: "Підтвердіть пароль",
        groupDevices: "Пристрої",
        whitelistTitle: "Білий список 2FA",
        whitelistDesc: "Спроби входу з цих IP пропускають другий фактор. Використовуйте лише для захищених мереж.",
        ipAddressLabel: "IP-адреса",
        manualEntry: "Ввести вручну",
        useMyIp: "Використати мій IP",
        descriptionLabel: "Опис",
        descriptionPlaceholder: "Офіс, VPN тощо.",
        addIp: "Додати IP",
        loadingWhitelist: "Завантаження білого списку...",
        tableHeaderIp: "IP",
        tableHeaderDescription: "Опис",
        tableHeaderActions: "Дії",
        whitelistEmpty: "IP-адреси ще не додані.",
        edit: "Редагувати",
        delete: "Видалити",
        ipPlaceholder: "192.0.2.1",
        ipRequired: "IP є обов'язковим.",
        promptIpNote: "Додайте опис для цього IP",
        confirmRemoveIp: (ip) => `Видалити ${ip} з білого списку?`,
        whitelistLoadError: "Не вдалося завантажити білий список.",
        ipAdded: "IP додано.",
        ipAddFailed: "Не вдалося додати IP.",
        entryUpdated: "Запис оновлено.",
        entryUpdateFailed: "Не вдалося оновити запис.",
        entryRemoved: "Запис видалено.",
        entryRemoveFailed: "Не вдалося видалити запис.",
        devicesTitle: "Активні пристрої",
        devicesSubtitle: "Керуйте токенами, що мають доступ до вашого акаунту.",
        refresh: "Оновити",
        signOutAll: "Вийти на всіх пристроях",
        loadingDevices: "Завантаження пристроїв...",
        noActiveSessions: "Активних сесій не знайдено.",
        unnamedDevice: "Неназваний пристрій",
        currentBadge: "Поточний",
        ipUnavailable: "IP недоступний",
        lastActivePrefix: "Остання активність:",
        rename: "Перейменувати",
        signOut: "Вийти",
        promptDeviceName: "Введіть назву для цього пристрою",
        confirmSignOutDevice: "Вийти з цього пристрою?",
        confirmSignOutAll: "Вийти з усіх пристроїв?",
        devicesLoadError: "Не вдалося завантажити активні пристрої.",
        deviceRenameSuccess: "Назву пристрою оновлено.",
        deviceRenameError: "Не вдалося оновити пристрій.",
        deviceRevokeSuccess: "Пристрій від'єднано.",
        deviceRevokeError: "Не вдалося від'єднати пристрій.",
        deviceRevokeAllSuccess: "Вихід виконано на всіх пристроях.",
        deviceRevokeAllError: "Не вдалося вийти на всіх пристроях.",
        noDataDash: "—",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

const Profile = (props) => {
    const { user, setUserData } = useAuth();
    const lang = (props?.lang || getInitialLang());
    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const [fieldsData, setFieldsData] = useState({})
    const [response, setResponse] = useState({});
    const [prefilledUserId, setPrefilledUserId] = useState(null);
    const [timeZones, setTimeZones] = useState([]);
    const [timeZonesLoading, setTimeZonesLoading] = useState(false);
    const [saveFeedback, setSaveFeedback] = useState({ type: "", message: "" });
    const [isSaving, setIsSaving] = useState(false);
    const latestFieldsRef = useRef(fieldsData);

    const navigate = useNavigate();

    const mapUserToFields = (userData = {}) => ({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        phone: userData.phone || "",
        location: userData.location || userData.city || "",
        address: userData.address || userData.country || "",
        time_zone: userData.time_zone !== undefined && userData.time_zone !== null
            ? `${userData.time_zone}`
            : "",
        two_factor_enabled: Boolean(
            userData.two_factor_enabled ??
            userData.two_factor_auth ??
            userData.two_factor ??
            false
        ),
    });

    const handleFieldChange = (event, key, itemId = null) => {
        if (itemId !== null) {
            setFieldsData((prev) => {
                const current = Array.isArray(prev[key]) ? [...prev[key]] : [];
                const valueIndex = current.indexOf(itemId);
                if (valueIndex > -1) {
                    current.splice(valueIndex, 1);
                } else {
                    current.push(itemId);
                }
                return { ...prev, [key]: current };
            });
            return;
        }

        let value;
        if (event?.target?.type === "checkbox") {
            value = Boolean(event.target.checked);
        } else if (event && event.target && "value" in event.target) {
            value = event.target.value;
        } else {
            value = event?.target?.value ?? event;
        }
        setFieldsData((prev) => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        latestFieldsRef.current = fieldsData;
    }, [fieldsData]);

    const userIdentity = user?.user_id ?? user?.id ?? null;
    const normalizeTimeZones = (payload) => {
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.rows)) return payload.rows;
        if (Array.isArray(payload)) return payload;
        return [];
    };

    useEffect(() => {
        if (!userIdentity) {
            setFieldsData({});
            setPrefilledUserId(null);
            return;
        }

        if (prefilledUserId === userIdentity) {
            return;
        }

        setFieldsData((prev) => ({
            ...prev,
            ...mapUserToFields(user),
        }));
        setPrefilledUserId(userIdentity);
    }, [user, userIdentity, prefilledUserId]);

    useEffect(() => {
        if (!user?.token) {
            setTimeZones([]);
            setTimeZonesLoading(false);
            return;
        }

        setTimeZonesLoading(true);

        const listeners = [
            {
                name: "time_zones",
                fn: "list",
                args: { items_count: 500 },
                saveDataFN: (payload) => {
                    setTimeZones(normalizeTimeZones(payload));
                    setTimeZonesLoading(false);
                },
            },
        ];

        const cleanup = socketUseEffect(listeners, user);
        return () => {
            setTimeZonesLoading(false);
            cleanup && cleanup();
        };
    }, [user?.token, user?.user_id]);

    const timeZoneOptions = useMemo(() => {
        if (timeZonesLoading) {
            return [{ id: "", name: t.tzLoading }];
        }

        if (!timeZones.length) {
            return [{ id: "", name: t.tzEmpty }];
        }

        const options = timeZones.map((tz) => {
            const optionId = tz.id ?? tz.time_zone ?? "";
            const label = tz.name && tz.time_zone
                ? `${tz.name} (${tz.time_zone})`
                : (tz.name || tz.time_zone || "");

            return {
                id: optionId !== "" ? `${optionId}` : "",
                name: label,
            };
        }).filter((tz) => tz.id && tz.name);

        return [{ id: "", name: t.tzSelect }, ...options];
    }, [timeZones, timeZonesLoading, t]);

    const validateProfileFields = () => {
        const errors = {};
        const firstName = (fieldsData.first_name || "").trim();
        const lastName = (fieldsData.last_name || "").trim();
        const email = (fieldsData.email || "").trim();

        if (!firstName) errors.first_name = t.firstNameRequired;
        if (!lastName) errors.last_name = t.lastNameRequired;
        if (!email) {
            errors.email = t.emailRequired;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errors.email = t.emailInvalid;
        }

        const currentPass = (fieldsData.cur_pass || "").trim();
        const newPass = (fieldsData.new_pass || "").trim();
        const confirmPass = (fieldsData.conf_pass || "").trim();
        const wantsPasswordChange = currentPass || newPass || confirmPass;

        if (wantsPasswordChange) {
            if (!currentPass) {
                errors.cur_pass = t.currentPassRequired;
            }
            if (!newPass) {
                errors.new_pass = t.newPassRequired;
            }
            if (!confirmPass) {
                errors.conf_pass = t.confirmPassRequired;
            }
            if (newPass && confirmPass && newPass !== confirmPass) {
                errors.conf_pass = t.passwordMismatch;
            }
            if (newPass && newPass.length < 8) {
                errors.new_pass = t.passwordTooShort;
            }
        }

        setResponse((prev) => ({ ...prev, err: errors }));
        return Object.keys(errors).length === 0;
    };

    const buildProfilePayload = () => {
        const payload = {
            first_name: fieldsData.first_name?.trim(),
            last_name: fieldsData.last_name?.trim(),
            email: fieldsData.email?.trim(),
            phone: fieldsData.phone?.trim(),
            location: fieldsData.location?.trim(),
            address: fieldsData.address?.trim(),
        };

        const timeZoneValue = fieldsData.time_zone;
        if (timeZoneValue !== undefined && timeZoneValue !== null && timeZoneValue !== "") {
            const parsedTimeZone = Number(timeZoneValue);
            if (!Number.isNaN(parsedTimeZone)) {
                payload.time_zone = parsedTimeZone;
            }
        }

        if (userIdentity) {
            payload.user_id = userIdentity;
        }

        if (typeof fieldsData.two_factor_enabled !== "undefined") {
            payload.two_factor_enabled = fieldsData.two_factor_enabled ? 1 : 0;
        }

        const currentPass = (fieldsData.cur_pass || "").trim();
        const newPass = (fieldsData.new_pass || "").trim();
        const confirmPass = (fieldsData.conf_pass || "").trim();
        if (currentPass || newPass || confirmPass) {
            payload.current_password = currentPass;
            payload.new_password = newPass;
            payload.confirm_password = confirmPass;
        }

        return payload;
    };

    const handleSaveProfile = () => {
        if (isSaving) return;
        if (!user?.token) {
            setSaveFeedback({ type: "error", message: t.missingToken });
            return;
        }
        if (!validateProfileFields()) return;

        setIsSaving(true);
        setSaveFeedback({ type: "", message: "" });

        socket.emit("profile:update", {
            token: user.token,
            fields: buildProfilePayload(),
        });
    };

    useEffect(() => {
        const handleProfileResponse = (payload = {}) => {
            setIsSaving(false);

            const scrollToTop = () => {
                try {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                } catch (_) {
                    window.scrollTo(0, 0);
                }
            };

            if (payload.status === 1) {
                setSaveFeedback({ type: "success", message: t.saveSuccess });
                setResponse({ status: 1, err: {} });

                const serverData = payload.data || {};
                const mergedFields = {
                    ...latestFieldsRef.current,
                    ...serverData,
                };

                setFieldsData((prev) => ({
                    ...prev,
                    ...serverData,
                }));
                latestFieldsRef.current = mergedFields;

                if (
                    !serverData.current_password &&
                    typeof setUserData === "function"
                ) {
                    setUserData({
                        ...user,
                        ...serverData,
                    });
                }
                if (serverData.email) {
                    localStorage.setItem("user_email", serverData.email);
                }
                if (serverData.password_updated || fieldsData.cur_pass || fieldsData.new_pass || fieldsData.conf_pass) {
                    setFieldsData((prev) => ({
                        ...prev,
                        cur_pass: "",
                        new_pass: "",
                        conf_pass: "",
                    }));
                    latestFieldsRef.current = {
                        ...latestFieldsRef.current,
                        cur_pass: "",
                        new_pass: "",
                        conf_pass: "",
                    };
                }
                scrollToTop();
            } else {
                const errMessage = payload?.err?.general || payload?.err?.message || t.saveErrorGeneric;
                setSaveFeedback({ type: "error", message: errMessage });
                if (payload?.err) {
                    setResponse((prev) => ({
                        ...prev,
                        err: { ...(prev?.err || {}), ...payload.err },
                    }));
                }
                scrollToTop();
            }
        };

        socket.on("profile:update-response", handleProfileResponse);
        return () => {
            socket.off("profile:update-response", handleProfileResponse);
        };
    }, [setUserData, t, user]);

    const twoFAComponent = () => {
        const enabled = Boolean(fieldsData.two_factor_enabled);

        const toggleTwoFA = () => {
            handleFieldChange({ target: { type: "checkbox", checked: !enabled } }, "two_factor_enabled");
        };

        return (
            <div className="card">
                <div className="card-body p-4">
                    <h4 className="fw-semibold mb-3">{t.twoFaCardTitle}</h4>
                    <div className="d-flex align-items-center justify-content-between pb-3">
                        <p className="mb-0 text-muted">
                            {t.twoFaLead}
                        </p>
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="two-fa-toggle"
                                checked={enabled}
                                onChange={toggleTwoFA}
                            />
                            <label className="form-check-label" htmlFor="two-fa-toggle">
                                {enabled ? t.twoFaEnabled : t.twoFaDisabled}
                            </label>
                        </div>
                    </div>
                    <div className="text-muted small border-top pt-3">
                        {t.twoFaFooter}
                    </div>
                </div>
            </div>
        )
    }

    const fields = [
        {
            type: "tab",
            name: t.tabAccount,
            image: "ti ti-home-2 fs-4",
            view: "cycle",
            fields: [
                [
                    {
                        label: t.groupPersonal,
                        subLabel: t.groupPersonalSub,
                        type: "group",
                        fields: [
                            [
                                { label: t.firstNameLabel, key: "first_name", showLabel: "top" },
                                { label: t.lastNameLabel, key: "last_name", showLabel: "top" },
                            ],
                            [
                                { label: t.emailLabel, key: "email", showLabel: "top" },
                                { label: t.phoneLabel, key: "phone", showLabel: "top" },
                            ],
                            [
                                { label: t.locationLabel, key: "location", showLabel: "top" },
                                { label: t.addressLabel, key: "address", showLabel: "top" },
                            ],
                        ],
                    },
                ],
                [
                    {
                        label: t.groupDateTime,
                        subLabel: t.groupDateTimeSub,
                        type: "group",
                        fields: [
                            [
                                { label: t.timeZoneLabel, key: "time_zone", type: "selectbox", data: timeZoneOptions },
                            ],
                        ],
                        columns: 8,
                    },
                ],
                [
                    {
                        label: t.groupPassword,
                        subLabel: t.groupPasswordSub,
                        type: "group",
                        fields: [
                            [
                                { label: t.currentPasswordLabel, key: "cur_pass", type: "password" },
                                { label: t.newPasswordLabel, key: "new_pass", type: "password" },
                                { label: t.confirmPasswordLabel, key: "conf_pass", type: "password" },
                            ],
                        ],
                    },
                ],
            ],
        },
        {
            type: "tab",
            name: t.tabSecurity,
            image: "ti ti-user fs-4",
            fields: [
                [
                    {
                        label: t.twoFaCardTitle,
                        type: "group",
                        fields: [
                            [{ type: "custom", fn: twoFAComponent }],
                            [{ type: "custom", fn: () => <WhitelistCard token={user?.token} lang={lang} /> }],
                        ],
                        columns: 6,
                    },
                    {
                        label: t.groupDevices,
                        type: "custom",
                        fn: () => <DevicesCard token={user?.token} lang={lang} />,
                        columns: 6,
                    },
                ],
            ],
        },
    ];

    return (
        <>
            <PageTitle title={t.pageTitle} homeUrl={"/dashboard"} />
            {saveFeedback.message && (
                <div className={`alert alert-${saveFeedback.type === "error" ? "danger" : "success"} mt-3`}>
                    {saveFeedback.message}
                </div>
            )}
            <RenderFormFields fields={fields} fieldsData={fieldsData} fieldsChange={handleFieldChange} response={response} isForm={true} cancelAction={() => {navigate(`/dashboard`, { replace: true });}} saveAction={handleSaveProfile} />
        </>
    )
}

export  default  Profile;

const DevicesCard = React.memo(({ token, lang }) => {
    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const [tokensState, setTokensState] = useState({ loading: false, error: "", items: [] });
    const [devicesProcessing, setDevicesProcessing] = useState(false);
    const [devicesFeedback, setDevicesFeedback] = useState({ type: "", message: "" });
    const initialFetchRef = useRef(false);

    const formatDateTime = (value) => {
        if (!value) return "";
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return `${value}`;
        }
        return parsed.toLocaleString();
    };

    const requestDevicesList = useCallback(() => {
        if (!token) {
            setTokensState({ loading: false, error: "", items: [] });
            return;
        }
        setTokensState((prev) => ({ ...prev, loading: true, error: "" }));
        socket.emit("profile_devices:list", { token });
    }, [token]);

    const emitMutation = useCallback((eventName, payload = {}) => {
        if (!token) return;
        setDevicesFeedback({ type: "", message: "" });
        setDevicesProcessing(true);
        socket.emit(eventName, { token, ...payload });
    }, [token]);

    useEffect(() => {
        const handleList = (payload = {}) => {
            if (payload.status === 1) {
                const items = Array.isArray(payload.items)
                    ? payload.items
                    : Array.isArray(payload.data)
                        ? payload.data
                        : [];
                setTokensState({ loading: false, error: "", items });
            } else {
                setTokensState({
                    loading: false,
                    error: payload.message || t.devicesLoadError,
                    items: [],
                });
            }
        };

        const handleRename = (payload = {}) => {
            setDevicesProcessing(false);
            if (payload.status === 1) {
                setDevicesFeedback({ type: "success", message: payload.message || t.deviceRenameSuccess });
                requestDevicesList();
            } else {
                setDevicesFeedback({ type: "error", message: payload.message || t.deviceRenameError });
            }
        };

        const handleRevoke = (payload = {}) => {
            setDevicesProcessing(false);
            if (payload.status === 1) {
                setDevicesFeedback({ type: "success", message: payload.message || t.deviceRevokeSuccess });
                requestDevicesList();
            } else {
                setDevicesFeedback({ type: "error", message: payload.message || t.deviceRevokeError });
            }
        };

        const handleRevokeAll = (payload = {}) => {
            setDevicesProcessing(false);
            if (payload.status === 1) {
                setDevicesFeedback({
                    type: "success",
                    message: payload.message || t.deviceRevokeAllSuccess,
                });
                requestDevicesList();
            } else {
                setDevicesFeedback({
                    type: "error",
                    message: payload.message || t.deviceRevokeAllError,
                });
            }
        };

        socket.on("profile_devices:list-data", handleList);
        socket.on("profile_devices:rename-response", handleRename);
        socket.on("profile_devices:revoke-response", handleRevoke);
        socket.on("profile_devices:revoke-all-response", handleRevokeAll);

        return () => {
            socket.off("profile_devices:list-data", handleList);
            socket.off("profile_devices:rename-response", handleRename);
            socket.off("profile_devices:revoke-response", handleRevoke);
            socket.off("profile_devices:revoke-all-response", handleRevokeAll);
        };
    }, [requestDevicesList, t, token]);

    useEffect(() => {
        if (token) {
            if (!initialFetchRef.current) {
                initialFetchRef.current = true;
                requestDevicesList();
            }
        } else {
            initialFetchRef.current = false;
            setTokensState({ loading: false, error: "", items: [] });
        }
    }, [requestDevicesList, token]);

    const handleRename = (item) => {
        const nextTitle = window.prompt(t.promptDeviceName, item.title || item.device || "");
        if (nextTitle === null) return;
        const trimmed = nextTitle.trim();
        if (!trimmed || trimmed === item.title) return;
        emitMutation("profile_devices:rename", { token_id: item.id, title: trimmed });
    };

    const handleRevoke = (item) => {
        if (!window.confirm(t.confirmSignOutDevice)) {
            return;
        }
        emitMutation("profile_devices:revoke", { token_id: item.id });
    };

    const handleRevokeAll = () => {
        if (!window.confirm(t.confirmSignOutAll)) {
            return;
        }
        emitMutation("profile_devices:revoke-all");
    };

    return (
        <div className="card">
            <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                        <div className="text-bg-light rounded-1 p-3 d-inline-flex align-items-center justify-content-center me-3">
                            <i className="ti ti-device-laptop text-primary fs-5"></i>
                        </div>
                        <h5 className="fs-5 fw-semibold mb-1 d-inline">{t.devicesTitle}</h5>
                        <p className="mb-0 text-muted small">{t.devicesSubtitle}</p>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={requestDevicesList}
                            disabled={tokensState.loading || devicesProcessing}
                        >
                            {t.refresh}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={handleRevokeAll}
                            disabled={devicesProcessing || !tokensState.items.length}
                        >
                            {t.signOutAll}
                        </button>
                    </div>
                </div>

                {devicesFeedback.message && (
                    <div className={`alert alert-${devicesFeedback.type === "error" ? "danger" : "success"} py-2`}>
                        {devicesFeedback.message}
                    </div>
                )}

                {tokensState.error && (
                    <div className="alert alert-danger py-2">{tokensState.error}</div>
                )}

                {tokensState.loading && (
                    <div className="text-muted small">{t.loadingDevices}</div>
                )}

                {!tokensState.loading && tokensState.items.length === 0 && !tokensState.error && (
                    <div className="text-muted small">{t.noActiveSessions}</div>
                )}

                <div className="d-flex flex-column gap-3">
                    {tokensState.items.map((item) => (
                        <div
                            key={item.id}
                            className="d-flex align-items-start justify-content-between border rounded px-3 py-2"
                        >
                            <div className="me-3">
                                <div className="fw-semibold">
                                    {item.title || item.device || t.unnamedDevice}
                                    {item.is_current && (
                                        <span className="badge bg-primary-subtle text-primary ms-2">{t.currentBadge}</span>
                                    )}
                                </div>
                                {([item.device, item.platform].filter(Boolean).length > 0) && (
                                    <div className="text-muted small">
                                        {[item.device, item.platform].filter(Boolean).join(" · ")}
                                    </div>
                                )}
                                <div className="text-muted small">
                                    {item.location ? `${item.location} · ` : ""}
                                    {item.ip || t.ipUnavailable}
                                </div>
                                <div className="text-muted small">
                                    {t.lastActivePrefix} {formatDateTime(item.last_active_at) || t.noDataDash}
                                </div>
                            </div>
                            <div className="btn-group btn-group-sm">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => handleRename(item)}
                                    disabled={devicesProcessing}
                                >
                                    {t.rename}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={() => handleRevoke(item)}
                                    disabled={devicesProcessing}
                                >
                                    {t.signOut}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}, (prev, next) => prev.token === next.token && prev.lang === next.lang && !!prev.token === !!next.token);

const WhitelistCard = React.memo(({ token, lang }) => {
    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const [listState, setListState] = useState({ loading: false, error: "", items: [] });
    const [feedback, setFeedback] = useState({ type: "", message: "" });
    const [form, setForm] = useState({ ip: "", note: "", useCurrent: false });
    const [currentIP, setCurrentIP] = useState("");
    const [processing, setProcessing] = useState(false);
    const initialFetchRef = useRef(false);

    const requestList = useCallback(() => {
        if (!token) {
            setListState({ loading: false, error: "", items: [] });
            return;
        }
        setListState((prev) => ({ ...prev, loading: true, error: "" }));
        socket.emit("profile_whitelist:list", { token });
    }, [token]);

    const emitMutation = useCallback((event, payload = {}) => {
        if (!token) return;
        setFeedback({ type: "", message: "" });
        setProcessing(true);
        socket.emit(event, { token, ...payload });
    }, [token]);

    useEffect(() => {
        const handleList = (payload = {}) => {
            if (payload.status === 1) {
                const items = Array.isArray(payload.items)
                    ? payload.items
                    : Array.isArray(payload.data)
                        ? payload.data
                        : [];
                setListState({ loading: false, error: "", items });
            } else {
                setListState({
                    loading: false,
                    error: payload.message || t.whitelistLoadError,
                    items: [],
                });
            }
        };

        const handleAdd = (payload = {}) => {
            setProcessing(false);
            if (payload.status === 1) {
                setFeedback({ type: "success", message: payload.message || t.ipAdded });
                setForm({ ip: "", note: "", useCurrent: false });
                requestList();
            } else {
                setFeedback({ type: "error", message: payload.message || t.ipAddFailed });
            }
        };

        const handleUpdate = (payload = {}) => {
            setProcessing(false);
            if (payload.status === 1) {
                setFeedback({ type: "success", message: payload.message || t.entryUpdated });
                requestList();
            } else {
                setFeedback({ type: "error", message: payload.message || t.entryUpdateFailed });
            }
        };

        const handleDelete = (payload = {}) => {
            setProcessing(false);
            if (payload.status === 1) {
                setFeedback({ type: "success", message: payload.message || t.entryRemoved });
                requestList();
            } else {
                setFeedback({ type: "error", message: payload.message || t.entryRemoveFailed });
            }
        };

        socket.on("profile_whitelist:list-data", handleList);
        socket.on("profile_whitelist:add-response", handleAdd);
        socket.on("profile_whitelist:update-response", handleUpdate);
        socket.on("profile_whitelist:delete-response", handleDelete);

        return () => {
            socket.off("profile_whitelist:list-data", handleList);
            socket.off("profile_whitelist:add-response", handleAdd);
            socket.off("profile_whitelist:update-response", handleUpdate);
            socket.off("profile_whitelist:delete-response", handleDelete);
        };
    }, [requestList, t, token]);

    useEffect(() => {
        if (token) {
            if (!initialFetchRef.current) {
                initialFetchRef.current = true;
                requestList();
                socket.emit("profile_tools:ip", { token });
            }
        } else {
            initialFetchRef.current = false;
            setListState({ loading: false, error: "", items: [] });
            setCurrentIP("");
        }
    }, [requestList, token]);

    useEffect(() => {
        const handleIp = (payload = {}) => {
            if (payload.status === 1 && payload.ip) {
                setCurrentIP(payload.ip);
            }
        };

        socket.on("profile_tools:ip-data", handleIp);
        return () => socket.off("profile_tools:ip-data", handleIp);
    }, []);

    const handleAdd = () => {
        const ip = (form.useCurrent ? currentIP : form.ip).trim();
        const note = form.note.trim();
        if (!ip) {
            setFeedback({ type: "error", message: t.ipRequired });
            return;
        }
        emitMutation("profile_whitelist:add", { ip, note });
    };

    const handleEdit = (item) => {
        const nextNote = window.prompt(t.promptIpNote, item.note || "");
        if (nextNote === null) return;
        emitMutation("profile_whitelist:update", { id: item.id, note: nextNote.trim() });
    };

    const handleDelete = (item) => {
        if (!window.confirm(t.confirmRemoveIp(item.ip))) return;
        emitMutation("profile_whitelist:delete", { id: item.id });
    };

    return (
        <div className="card">
            <div className="card-body p-4">
                <h5 className="fw-semibold mb-1">{t.whitelistTitle}</h5>
                <p className="text-muted small mb-3">
                    {t.whitelistDesc}
                </p>
                {feedback.message && (
                    <div className={`alert alert-${feedback.type === "error" ? "danger" : "success"} py-2`}>
                        {feedback.message}
                    </div>
                )}
                {listState.error && (
                    <div className="alert alert-danger py-2">{listState.error}</div>
                )}
                <div className="mb-3">
                    <label className="form-label">{t.ipAddressLabel}</label>
                    <div className="input-group">
                        <input
                            className="form-control"
                            placeholder={currentIP ? currentIP : t.ipPlaceholder}
                            value={form.useCurrent ? currentIP : form.ip}
                            onChange={(e) => setForm((prev) => ({ ...prev, ip: e.target.value, useCurrent: false }))}
                            disabled={form.useCurrent}
                        />
                        <button
                            className="btn btn-outline-secondary"
                            type="button"
                            disabled={!currentIP}
                            onClick={() => setForm((prev) => ({ ...prev, useCurrent: !prev.useCurrent }))}
                        >
                            {form.useCurrent ? t.manualEntry : t.useMyIp}
                        </button>
                    </div>
                </div>
                <div className="mb-3">
                    <label className="form-label">{t.descriptionLabel}</label>
                    <input
                        className="form-control"
                        placeholder={t.descriptionPlaceholder}
                        value={form.note}
                        onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                    />
                </div>
                <div className="mb-4 d-grid">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAdd}
                        disabled={processing}
                    >
                        {t.addIp}
                    </button>
                </div>
                    {listState.loading ? (
                        <div className="text-muted small">{t.loadingWhitelist}</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>{t.tableHeaderIp}</th>
                                        <th>{t.tableHeaderDescription}</th>
                                        <th style={{ width: "140px" }}>{t.tableHeaderActions}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listState.items.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className="text-muted small">
                                                {t.whitelistEmpty}
                                            </td>
                                        </tr>
                                    ) : (
                                        listState.items.map((item) => (
                                            <tr key={item.id}>
                                                <td className="fw-semibold">{item.ip}</td>
                                                <td className="text-muted">{item.note || t.noDataDash}</td>
                                                <td>
                                                    <div className="btn-group btn-group-sm">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary"
                                                            onClick={() => handleEdit(item)}
                                                            disabled={processing}
                                                        >
                                                            {t.edit}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger"
                                                            onClick={() => handleDelete(item)}
                                                            disabled={processing}
                                                        >
                                                            {t.delete}
                                                        </button>
                                                    </div>
                                                </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}, (prev, next) => prev.token === next.token && prev.lang === next.lang && !!prev.token === !!next.token);
